/* eslint-disable react/style-prop-object --
   The DS ui/Button exposes a variant prop literally named `style`
   (style="stroke" | ...), which this rule mistakes for the DOM style object. */
import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Dialog } from '@/components/Dialog';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { Button } from '@/components/ui/button';
import { FilterParams, warehouseExport } from '@/services/api/warehouse';

// The logistic web's "Download CSV" on Stock: three reports, all hubs, pinned
// to the signed-in client by the backend.

type Report = 'inventory' | 'movements' | 'mutations';

/** Movement / mutation reports cover at most this many days (inclusive). */
export const MAX_PERIOD_DAYS = 31;

const REPORTS: { value: Report; label: string; hint: string }[] = [
  {
    value: 'inventory',
    label: 'Inventory',
    hint: 'Stok saat ini per batch di semua hub — sesuai pencarian di tabel.',
  },
  {
    value: 'movements',
    label: 'Pergerakan stok',
    hint: 'Laporan Stock: setiap barang masuk, keluar dan transfer dalam periode.',
  },
  {
    value: 'mutations',
    label: 'Mutasi stok',
    hint: 'Laporan Mutasi: stok awal, masuk, keluar dan stok akhir per SKU per hari.',
  },
];

const inputCls =
  'h-10 w-full rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-3 text-sm text-text-strong-950 focus:outline-none focus:ring-2 focus:ring-primary';

/** Why a period can't be downloaded, or null when it can. */
export const periodError = (from: string, to: string): string | null => {
  if (!from || !to) return 'Isi tanggal mulai dan selesai.';
  const days = dayjs(to).diff(dayjs(from), 'day') + 1;
  if (days < 1) return 'Tanggal mulai harus sebelum tanggal selesai.';
  if (days > MAX_PERIOD_DAYS)
    return `Periode maksimal ${MAX_PERIOD_DAYS} hari.`;
  return null;
};

const StockExportDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  /** The stock table's search + filters — the inventory CSV follows them. */
  search?: string;
  filters?: FilterParams;
}> = ({ open, onClose, search, filters }) => {
  const [report, setReport] = useState<Report>('inventory');
  const [from, setFrom] = useState(() =>
    dayjs().startOf('month').format('YYYY-MM-DD')
  );
  const [to, setTo] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [busy, setBusy] = useState(false);

  const isPeriod = report !== 'inventory';
  const error = isPeriod ? periodError(from, to) : null;
  const label = REPORTS.find((r) => r.value === report)?.label ?? '';

  const download = async () => {
    setBusy(true);
    try {
      if (report === 'inventory')
        await warehouseExport.stocks({ search }, filters);
      else if (report === 'movements')
        await warehouseExport.stockMovements({ dateFrom: from, dateTo: to });
      else await warehouseExport.stockMutations({ dateFrom: from, dateTo: to });
      showSuccessToast('CSV di-download', `CSV ${label} berhasil di-download.`);
      onClose();
    } catch (err) {
      showErrorToast(
        `CSV ${label} gagal di-download`,
        err instanceof Error ? err.message : String(err)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Download CSV"
      description="Stok Anda di semua hub Dash."
      icon={<i className="ri-download-2-line text-lg text-primary" />}
      size="md"
      footer={
        <>
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Batal
          </Button>
          <Button loading={busy} disabled={!!error} onClick={download}>
            Download
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium text-text-strong-950">
            Jenis laporan
          </legend>
          {REPORTS.map((r) => (
            <label
              key={r.value}
              className={`flex cursor-pointer gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                report === r.value
                  ? 'border-primary bg-feature-lighter'
                  : 'border-stroke-soft-200 hover:bg-bg-weak-50'
              }`}
            >
              <input
                type="radio"
                name="stock-report"
                value={r.value}
                checked={report === r.value}
                onChange={() => setReport(r.value)}
                className="mt-1 accent-primary"
              />
              <span>
                <span className="block text-sm font-medium text-text-strong-950">
                  {r.label}
                </span>
                <span className="block text-xs text-text-sub-600">
                  {r.hint}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        {isPeriod && (
          <div>
            <p className="mb-2 text-sm font-medium text-text-strong-950">
              Periode (maks. {MAX_PERIOD_DAYS} hari)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-text-sub-600">
                Dari
                <input
                  type="date"
                  className={`${inputCls} mt-1`}
                  value={from}
                  max={to || undefined}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className="text-xs text-text-sub-600">
                Sampai
                <input
                  type="date"
                  className={`${inputCls} mt-1`}
                  value={to}
                  min={from || undefined}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
            </div>
            {error && <p className="mt-2 text-xs text-error-base">{error}</p>}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default StockExportDialog;
